import React, { Suspense, lazy, Component, type ReactNode, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { trackPageView } from './lib/activityTracker';

// Tab visibility title animation
const TAB_MESSAGES = [
  '👋 Nereye gittin?',
  '🎯 Harika fırsatlar seni bekliyor!',
  '🛒 Sepetini unutma!',
  '💰 En uygun fiyatlar AR Klima\'da!',
  '🌟 Geri gel, indirimler devam ediyor!',
  '❄️ Serinlemek için geri dön!',
];
let tabMsgInterval: ReturnType<typeof setInterval> | null = null;
let originalTitle = '';
let tabMsgIdx = 0;

function startTitleAnimation() {
  if (!originalTitle) originalTitle = document.title;
  tabMsgIdx = 0;
  tabMsgInterval = setInterval(() => {
    document.title = TAB_MESSAGES[tabMsgIdx % TAB_MESSAGES.length];
    tabMsgIdx++;
  }, 1800);
}

function stopTitleAnimation() {
  if (tabMsgInterval) {
    clearInterval(tabMsgInterval);
    tabMsgInterval = null;
  }
  if (originalTitle) document.title = originalTitle;
}

function initTabVisibility() {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      startTitleAnimation();
    } else {
      stopTitleAnimation();
    }
  });
}

// Layouts (eagerly loaded)
import StorefrontLayout from './layouts/StorefrontLayout';
import AdminLayout from './layouts/AdminLayout';
import AuthLayout from './layouts/AuthLayout';

// Toast notifications (eager)
import ToastContainer from './shared/ui/ToastContainer';

// Auth store (admin only)
import { useAuthStore } from './stores/authStore';
import { useSettingsStore } from './stores/settingsStore';
import { Navigate } from 'react-router-dom';

// Storefront pages (lazy)
const HomePage = lazy(() => import('./pages/storefront/HomePage'));
const CategoryPage = lazy(() => import('./pages/storefront/CategoryPage'));
const ProductDetailPage = lazy(() => import('./pages/storefront/ProductDetailPage'));
const SearchPage = lazy(() => import('./pages/storefront/SearchPage'));
const CartPage = lazy(() => import('./pages/storefront/CartPage'));
const CheckoutPage = lazy(() => import('./pages/storefront/CheckoutPage'));
const WishlistPage = lazy(() => import('./pages/storefront/WishlistPage'));
const BrandPage = lazy(() => import('./pages/storefront/BrandPage'));
const BlogPage = lazy(() => import('./pages/storefront/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/storefront/BlogDetailPage'));
const CampaignPage = lazy(() => import('./pages/storefront/CampaignPage'));

// Auth pages (lazy) — Admin login only
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));

// Admin pages (lazy — bundle split)
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminProductForm = lazy(() => import('./pages/admin/AdminProductForm'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminOrderDetail = lazy(() => import('./pages/admin/AdminOrderDetail'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminPaymentRules = lazy(() => import('./pages/admin/AdminPaymentRules'));
const AdminBankAccounts = lazy(() => import('./pages/admin/AdminBankAccounts'));
const AdminContactSettings = lazy(() => import('./pages/admin/AdminContactSettings'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminImportExport = lazy(() => import('./pages/admin/AdminImportExport'));
const AdminSeo = lazy(() => import('./pages/admin/AdminSeo'));
const AdminShipping = lazy(() => import('./pages/admin/AdminShipping'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSupportTickets = lazy(() => import('./pages/admin/AdminSupportTickets'));
const AdminRoles = lazy(() => import('./pages/admin/AdminRoles'));
const AdminBrands = lazy(() => import('./pages/admin/AdminBrands'));
const AdminCampaigns = lazy(() => import('./pages/admin/AdminCampaigns'));
const AdminBanners = lazy(() => import('./pages/admin/AdminBanners'));
const AdminMediaLibrary = lazy(() => import('./pages/admin/AdminMediaLibrary'));
const AdminMenuBuilder = lazy(() => import('./pages/admin/AdminMenuBuilder'));
const AdminStaticPages = lazy(() => import('./pages/admin/AdminStaticPages'));
const AdminActivityLogs = lazy(() => import('./pages/admin/AdminActivityLogs'));
const AdminEmailSettings = lazy(() => import('./pages/admin/AdminEmailSettings'));
const AdminSmsSettings = lazy(() => import('./pages/admin/AdminSmsSettings'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const AdminInvoices = lazy(() => import('./pages/admin/AdminInvoices'));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'));
const AdminReceipts = lazy(() => import('./pages/admin/AdminReceipts'));

const AdminLiveChat = lazy(() => import('./pages/admin/AdminLiveChat'));
const AboutPage = lazy(() => import('./pages/info/AboutPage'));
const ContactPage = lazy(() => import('./pages/info/ContactPage'));
const FaqPage = lazy(() => import('./pages/info/FaqPage'));
const PrivacyPage = lazy(() => import('./pages/info/PrivacyPage'));
const CookiePage = lazy(() => import('./pages/info/CookiePage'));
const TermsPage = lazy(() => import('./pages/info/TermsPage'));

// Error pages (lazy)
const NotFoundPage = lazy(() => import('./pages/errors/NotFoundPage'));
const ServerErrorPage = lazy(() => import('./pages/errors/ServerErrorPage'));

// Loading fallback
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-10 h-10 rounded-full border-4 border-cyan-200 border-t-cyan-600 animate-spin" />
  </div>
);

// Error Boundary - beyaz ekran crash'lerini engeller
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4">
          <h2 className="text-xl font-bold text-gray-700">Bir hata oluştu</h2>
          <p className="text-gray-500 text-center">Sayfa yüklenirken beklenmeyen bir hata meydana geldi.</p>
          <a href="/" className="text-cyan-600 hover:underline">Anasayfaya dön</a>
        </div>
      );
    }
    return this.props.children;
  }
}

// Admin route guard
const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  // Also check localStorage as fallback for mock auth
  const hasToken = isAuthenticated || !!localStorage.getItem('auth_token');
  if (!hasToken) return <Navigate to="/giris" replace />;
  if (user && user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

// Route değişikliklerini takip eden hook
const usePageTracking = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
};

const AppContent: React.FC = () => {
  usePageTracking();
  const updateSiteSettings = useSettingsStore(state => state.updateSiteSettings);

  // Init tab visibility animation and fetch public settings on mount
  useEffect(() => {
    initTabVisibility();
    originalTitle = document.title;
    
    // Fetch dynamic site settings and categories from backend
    import('./api/client').then(({ apiService }) => {
      // Settings
      apiService.getPublicSettings().then(res => {
        if (res.data?.success || res.data) {
          const settings = res.data.data || res.data;
          updateSiteSettings(settings);
        }
      }).catch(err => console.error("Failed to fetch public settings", err));

      // Categories
      apiService.getCategories().then(res => {
        if (res.data?.success || res.data) {
          const categories = res.data.data || res.data;
          useSettingsStore.getState().setCategories(categories);
        }
      }).catch(err => console.error("Failed to fetch categories", err));
    });
  }, [updateSiteSettings]);

  return (
    <>
      <ToastContainer />
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Storefront */}
          <Route element={<StorefrontLayout />}>
            <Route index element={<HomePage />} />
            <Route path="kategori/:categorySlug" element={<CategoryPage />} />
            <Route path="kategori/:categorySlug/:subcategorySlug" element={<CategoryPage />} />
            <Route path="marka/:brandSlug" element={<BrandPage />} />
            <Route path="urun/:slug" element={<ProductDetailPage />} />
            <Route path="arama" element={<SearchPage />} />
            <Route path="sepet" element={<CartPage />} />
            <Route path="odeme" element={<CheckoutPage />} />
            <Route path="favoriler" element={<WishlistPage />} />
            <Route path="kampanyalar" element={<CampaignPage />} />
            <Route path="kampanyalar/:slug" element={<CampaignPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<BlogDetailPage />} />
            <Route path="hakkimizda" element={<AboutPage />} />
            <Route path="iletisim" element={<ContactPage />} />
            <Route path="sss" element={<FaqPage />} />
            <Route path="gizlilik-politikasi" element={<PrivacyPage />} />
            <Route path="cerez-politikasi" element={<CookiePage />} />
            <Route path="kullanim-kosullari" element={<TermsPage />} />
            <Route path="500" element={<ServerErrorPage />} />
          </Route>

          {/* Admin Login */}
          <Route element={<AuthLayout />}>
            <Route path="giris" element={<LoginPage />} />
          </Route>

          {/* Admin Panel */}
          <Route element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
            <Route path="admin" element={<AdminDashboard />} />
            <Route path="admin/analitik" element={<AdminAnalytics />} />
            <Route path="admin/urunler" element={<AdminProducts />} />
            <Route path="admin/urunler/yeni" element={<AdminProductForm />} />
            <Route path="admin/urunler/:id/duzenle" element={<AdminProductForm />} />
            <Route path="admin/kategoriler" element={<AdminCategories />} />
            <Route path="admin/markalar" element={<AdminBrands />} />
            <Route path="admin/kampanyalar" element={<AdminCampaigns />} />
            <Route path="admin/bannerlar" element={<AdminBanners />} />
            <Route path="admin/medya" element={<AdminMediaLibrary />} />
            <Route path="admin/siparisler" element={<AdminOrders />} />
            <Route path="admin/siparisler/:orderId" element={<AdminOrderDetail />} />
            <Route path="admin/faturalar" element={<AdminInvoices />} />
            <Route path="admin/odemeler" element={<AdminPayments />} />
            <Route path="admin/dekontlar" element={<AdminReceipts />} />
            <Route path="admin/musteriler" element={<AdminCustomers />} />
            <Route path="admin/kuponlar" element={<AdminCoupons />} />
            <Route path="admin/blog" element={<AdminBlog />} />
            <Route path="admin/kargo" element={<AdminShipping />} />
            <Route path="admin/seo" element={<AdminSeo />} />
            <Route path="admin/import-export" element={<AdminImportExport />} />
            <Route path="admin/yorumlar" element={<AdminReviews />} />
            <Route path="admin/destek" element={<AdminSupportTickets />} />
            <Route path="admin/bildirimler" element={<AdminNotifications />} />
            <Route path="admin/roller" element={<AdminRoles />} />
            <Route path="admin/ayarlar" element={<AdminSettings />} />
            <Route path="admin/email-ayarlari" element={<AdminEmailSettings />} />
            <Route path="admin/sms-ayarlari" element={<AdminSmsSettings />} />
            <Route path="admin/menu-yoneticisi" element={<AdminMenuBuilder />} />
            <Route path="admin/sayfalar" element={<AdminStaticPages />} />
            <Route path="admin/aktivite-gunlukleri" element={<AdminActivityLogs />} />
            <Route path="admin/canli-destek" element={<AdminLiveChat />} />
            <Route path="admin/odeme-kurallari" element={<AdminPaymentRules />} />
            <Route path="admin/banka-hesaplari" element={<AdminBankAccounts />} />
            <Route path="admin/iletisim-ayarlari" element={<AdminContactSettings />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </>
  );
};

const App: React.FC = () => {
  return <AppContent />;
};

export default App;
